import { useMemo, useState } from 'react';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    Divider,
    FormControlLabel,
    Typography
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DrawerContainer } from '../../../../components/Drawer/DrawerContainer';
import { DrawerHeaderWithLeftIcon } from '../../../../components/Drawer/DrawerHeader';
import { FallaVisita } from '../../consts/visitasTypes';
import {
    empresaYEstablecimiento,
    fechaDeFalla,
    idDeLaVisita,
    ordenarFallasRecienteAntigua
} from '../../consts/actaFallida';

interface DescargarActasFallidaDrawerProps {
    visita: any;
    fallas: FallaVisita[] | null;
    descargando: boolean;
    onDescargar: (idsEvento: number[]) => void;
    onCerrar: () => void;
}

interface ContenidoProps extends Omit<DescargarActasFallidaDrawerProps, 'fallas'> {
    fallas: FallaVisita[];
}

// La selección vive acá y el contenido se monta solo con el drawer abierto:
// cada apertura arranca sin ninguna falla marcada.
const Contenido = ({ visita, fallas, descargando, onDescargar, onCerrar }: ContenidoProps) => {
    const { t } = useTranslation('visitas');
    const [marcadas, setMarcadas] = useState<number[]>([]);
    const fallasOrdenadas = useMemo(() => ordenarFallasRecienteAntigua(fallas), [fallas]);
    const detalleVisita = empresaYEstablecimiento(visita);

    const todasMarcadas = marcadas.length === fallasOrdenadas.length;
    const algunasMarcadas = marcadas.length > 0 && !todasMarcadas;

    const alternarFalla = (idEvento: number) =>
        setMarcadas((actuales) =>
            actuales.includes(idEvento)
                ? actuales.filter((id) => id !== idEvento)
                : [...actuales, idEvento]
        );

    const alternarTodas = () =>
        setMarcadas(todasMarcadas ? [] : fallasOrdenadas.map((falla) => falla.idEvento));

    const descargar = () =>
        onDescargar(
            fallasOrdenadas
                .filter((falla) => marcadas.includes(falla.idEvento))
                .map((falla) => falla.idEvento)
        );

    return (
        <>
            <DrawerHeaderWithLeftIcon
                onIconClick={onCerrar}
                text={<strong>{t('table.actions.downloadActaFallida')}</strong>}
            />
            <Divider />

            <Box sx={{ bgcolor: 'grey.100', borderRadius: 1, px: 1.5, py: 1.25, mt: 2 }}>
                <Typography variant="body2">
                    <strong>{t('actaFallidaDrawer.visita')}</strong> #{idDeLaVisita(visita)}
                </Typography>
                {detalleVisita && (
                    <Typography variant="body2" color="text.secondary">
                        {detalleVisita}
                    </Typography>
                )}
            </Box>

            <FormControlLabel
                sx={{ mt: 2, mb: 1, mx: 0 }}
                control={
                    <Checkbox
                        checked={todasMarcadas}
                        indeterminate={algunasMarcadas}
                        onChange={alternarTodas}
                        disabled={descargando}
                    />
                }
                label={
                    <Typography sx={{ fontWeight: 600 }}>
                        {t('actaFallidaDrawer.seleccionarTodas')}
                    </Typography>
                }
            />
            <Divider />

            <Box sx={{ flex: 1, minHeight: 0, overflowY: 'auto', py: 1 }}>
                {fallasOrdenadas.map((falla) => (
                    <FormControlLabel
                        key={falla.idEvento}
                        sx={{ display: 'flex', alignItems: 'flex-start', mx: 0, py: 1 }}
                        control={
                            <Checkbox
                                checked={marcadas.includes(falla.idEvento)}
                                onChange={() => alternarFalla(falla.idEvento)}
                                disabled={descargando}
                                sx={{ pt: 0.25 }}
                            />
                        }
                        label={
                            <Box>
                                <Typography>{fechaDeFalla(falla)}</Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {falla.motivoDescripcion || '-'}
                                </Typography>
                            </Box>
                        }
                    />
                ))}
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    gap: 2,
                    pt: 2,
                    borderTop: 1,
                    borderColor: 'divider'
                }}
            >
                <Button variant="outlined" onClick={onCerrar} disabled={descargando}>
                    {t('actaFallidaDrawer.cancelar')}
                </Button>
                <Button
                    variant="contained"
                    onClick={descargar}
                    disabled={descargando || marcadas.length === 0}
                    startIcon={
                        descargando ? <CircularProgress size={16} color="inherit" /> : undefined
                    }
                >
                    {t('actaFallidaDrawer.descargar')}
                </Button>
            </Box>
        </>
    );
};

// Drawer de descarga del acta de visita fallida (SMP-599): se elige de qué
// fallas bajar el acta, una o varias.
const DescargarActasFallidaDrawer = ({
    visita,
    fallas,
    descargando,
    onDescargar,
    onCerrar
}: DescargarActasFallidaDrawerProps) => {
    const abierto = Boolean(visita && fallas?.length);
    // Mientras se generan las actas no se cierra: el aviso final llega con el
    // drawer todavía abierto.
    const cerrar = () => {
        if (!descargando) onCerrar();
    };

    return (
        <DrawerContainer open={abierto} onClose={cerrar}>
            {abierto && fallas && (
                <Contenido
                    visita={visita}
                    fallas={fallas}
                    descargando={descargando}
                    onDescargar={onDescargar}
                    onCerrar={cerrar}
                />
            )}
        </DrawerContainer>
    );
};

export default DescargarActasFallidaDrawer;
